import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { track } from '@/lib/tracking';
import { TrackingEventName } from '@/lib/types';
import { useGlobalContext } from '@/hooks/use-global-context';

import FormEmail from '@/components/forms/form-email';
import FormPhone from '@/components/forms/form-phone';
import FormSubmitButton from '@/components/forms/form-submit-button';
import Typography from '@/components/typography';

type FormValues = {
  email?: string;
  phone?: string;
};

interface ProspectFormProps {
  variant: 'email' | 'phone';
}

export default function ProspectForm({ variant }: ProspectFormProps) {
  const { pageMeta } = useGlobalContext();

  const formProps = useForm<FormValues>();

  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [showMoreLegalText, setShowMoreLegalText] = React.useState(false);

  const onSubmit = formProps.handleSubmit((data) => {
    track({
      pageMeta,
      name: TrackingEventName.ProspectFormSubmitButtonClicked,
      properties: {
        email: data.email,
        phone: data.phone,
        source: 'Content Page',
      },
    });

    setIsSubmitted(true);
  });

  if (isSubmitted) {
    return (
      <div className='mx-auto flex max-w-[240px] flex-col gap-4 rounded-lg bg-light-purple p-4 sm:p-6 lg:max-w-full'>
        <Typography.Subtitle2>Thank you!</Typography.Subtitle2>
        <Typography.Body35>
          A member of our team will be in touch with you shortly to discuss your
          job search.
        </Typography.Body35>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[240px] flex-col gap-4 rounded-lg bg-light-purple p-4 sm:p-6 lg:max-w-full'>
      <Typography.Subtitle2>We’re here to help!</Typography.Subtitle2>

      <Typography.Body35>
        Get connected with a Premier Navigator to help you find and apply to the
        next perfect role.
      </Typography.Body35>

      <FormProvider {...formProps}>
        <form onSubmit={onSubmit} className='flex flex-col gap-4'>
          {variant === 'email' && (
            <FormEmail name='email' label='Email' required />
          )}

          {variant === 'phone' && (
            <FormPhone name='phone' label='Phone Number' required />
          )}

          <FormSubmitButton className='justify-center'>
            Get Connected
          </FormSubmitButton>
        </form>
      </FormProvider>

      {variant === 'email' && (
        <Typography.Body5>
          By clicking “Get Connected” you agree to receive email communications
          about job opportunities.
        </Typography.Body5>
      )}
      {variant === 'phone' && (
        <>
          <Typography.Body5>
            Click “Get Connected” to permit Nomad Health and its affiliates to
            contact you by phone and SMS about job opportunities.{' '}
            {!showMoreLegalText && (
              <span
                className='
            underline cursor-pointer'
                onClick={() => setShowMoreLegalText(true)}
              >
                Read More.
              </span>
            )}
          </Typography.Body5>
          {showMoreLegalText && (
            <Typography.Body5>
              Message and data rates may apply. Message frequency varies.
              Consent to text is not required to participate in Nomad employment
              opportunities. Reply STOP to opt out of messages at any time.
            </Typography.Body5>
          )}
        </>
      )}
    </div>
  );
}
